import { useState, useMemo, useEffect, useCallback } from 'react';

import { debounce } from 'lodash';

import { getCustomersList } from '../../../api';

const DEBOUNCE_DELAY = 500;
const PAGE_SIZE = 25;

/**
 * Custom hook for managing customer search with debouncing
 * @param isActiveOnly - Filter to show only active customers
 * @param initialSearchQuery - Initial search query value (optional)
 * @param staffId - Filter customers by staff ID (from unified staff filter)
 * @param myCustomers - Filter to current user's customers (from unified staff filter)
 * @returns Search state and fetch function factory
 */
export const useCustomerSearch = (
  isActiveOnly: boolean,
  initialSearchQuery = '',
  staffId?: number,
  myCustomers?: boolean,
) => {
  const [searchQuery, setSearchQuery] = useState(initialSearchQuery);
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(initialSearchQuery);

  // Debounce search query using lodash (500ms for search UX)
  const debouncedSetSearch = useMemo(
    () =>
      debounce((value: string) => {
        setDebouncedSearchQuery(value);
      }, DEBOUNCE_DELAY),
    [],
  );

  // Cleanup debounce on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      debouncedSetSearch.cancel();
    };
  }, [debouncedSetSearch]);

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    debouncedSetSearch(value);
  };

  // Immediately execute search without waiting for debounce (e.g., on Enter key press)
  const flushSearch = useCallback(() => {
    debouncedSetSearch.flush();
  }, [debouncedSetSearch]);

  const fetchCustomersPage = useCallback(async(page: number) => {
    const response = await getCustomersList({
      page,
      page_size: PAGE_SIZE,
      search: debouncedSearchQuery || undefined,
      my_customers: myCustomers,
      is_active: isActiveOnly,
      ordering: '-created_at',
      staff_id: staffId,
    });

    return {
      results: response.results,
      next: response.next ?? null,
    };
  }, [debouncedSearchQuery, isActiveOnly, staffId, myCustomers]);

  return {
    searchQuery,
    setSearchQuery: handleSearchChange,
    fetchCustomersPage,
    debouncedSearchQuery,
    flushSearch,
  };
};
